import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus, RefreshCw, UserRound } from 'lucide-react';
import type { Grupo, Rol, UsuarioAdmin } from '@doc/shared';
import { api } from '../../lib/api.js';
import { ETIQUETA_ROL, fecha } from '../../lib/formato.js';
import { useSesion } from '../../lib/sesion.js';
import { Cabecera } from '../../layout/Marco.js';
import { Modal } from '../../ui/Modal.js';
import { CampoPassword } from '../../ui/CampoPassword.js';
import { generarPassword } from '../../lib/password.js';
import { useAvisos } from '../../ui/avisos.js';

const ROLES: Rol[] = ['MIEMBRO', 'ADMIN', 'SUPER_ADMIN'];

export function UsuariosPage() {
  const cliente = useQueryClient();
  const { usuario: yo } = useSesion();
  const { avisar, avisarError } = useAvisos();
  const [editando, setEditando] = useState<UsuarioAdmin | 'nuevo' | null>(null);

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<UsuarioAdmin[]>('/usuarios'),
  });
  const { data: grupos } = useQuery({ queryKey: ['grupos'], queryFn: () => api<Grupo[]>('/grupos') });

  const guardar = useMutation({
    mutationFn: (v: { id?: string; cuerpo: Record<string, unknown> }) =>
      v.id ? api(`/usuarios/${v.id}`, { metodo: 'PATCH', cuerpo: v.cuerpo }) : api('/usuarios', { metodo: 'POST', cuerpo: v.cuerpo }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['usuarios'] });
      avisar('Usuario guardado');
      setEditando(null);
    },
    onError: avisarError,
  });

  return (
    <>
      <Cabecera
        titulo="Usuarios"
        subtitulo="Cuentas de la organización y su pertenencia a grupos"
        acciones={
          <button className="btn btn--primario" onClick={() => setEditando('nuevo')}>
            <Plus size={14} /> Nuevo usuario
          </button>
        }
      />

      <div className="pagina">
        {isLoading && <div className="cargando">Cargando…</div>}
        {usuarios && (
          <table className="tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th style={{ width: 130 }}>Rol</th>
                <th>Grupos</th>
                <th style={{ width: 90 }}>Estado</th>
                <th style={{ width: 150 }}>Creado</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <UserRound size={14} style={{ color: 'var(--dim)' }} /> {u.nombre}
                    </div>
                  </td>
                  <td className="muted">{u.email}</td>
                  <td>
                    <span className={`badge${u.rol === 'MIEMBRO' ? '' : ' badge--verde'}`}>{ETIQUETA_ROL[u.rol]}</span>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {u.grupos.map((g) => g.nombre).join(', ') || '—'}
                  </td>
                  <td>
                    {u.activo ? <span className="badge badge--verde">Activa</span> : <span className="badge">Inactiva</span>}
                  </td>
                  <td className="muted">{fecha(u.creadoEn)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn--sm" onClick={() => setEditando(u)} disabled={u.id === yo?.id && u.rol !== 'SUPER_ADMIN'}>
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editando && (
        <FormularioUsuario
          usuario={editando === 'nuevo' ? null : editando}
          grupos={grupos ?? []}
          puedeAsignarRoles={yo?.rol === 'SUPER_ADMIN'}
          guardando={guardar.isPending}
          alGuardar={(cuerpo) => guardar.mutate({ id: editando === 'nuevo' ? undefined : editando.id, cuerpo })}
          alCerrar={() => setEditando(null)}
        />
      )}
    </>
  );
}

interface PropsFormulario {
  usuario: UsuarioAdmin | null;
  grupos: Grupo[];
  puedeAsignarRoles: boolean;
  guardando: boolean;
  alGuardar(cuerpo: Record<string, unknown>): void;
  alCerrar(): void;
}

function FormularioUsuario({ usuario, grupos, puedeAsignarRoles, guardando, alGuardar, alCerrar }: PropsFormulario) {
  const [nombre, setNombre] = useState(usuario?.nombre ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState<Rol>(usuario?.rol ?? 'MIEMBRO');
  const [activo, setActivo] = useState(usuario?.activo ?? true);
  const [miembroDe, setMiembroDe] = useState<Set<string>>(new Set(usuario?.grupos.map((g) => g.id) ?? []));
  const [generada, setGenerada] = useState(false);
  const [copiada, setCopiada] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(password);
      setCopiada(true);
      setTimeout(() => setCopiada(false), 2500);
    } catch {
      // Sin permiso de portapapeles queda el texto a la vista para copiarlo a mano.
    }
  }

  const enviar = () => {
    const grupoIds = [...miembroDe];
    if (usuario) {
      alGuardar({ nombre, rol, activo, grupos: grupoIds, ...(password ? { password } : {}) });
    } else {
      alGuardar({ nombre, email, password, rol, grupos: grupoIds });
    }
  };

  return (
    <Modal
      titulo={usuario ? `Editar a ${usuario.nombre}` : 'Nuevo usuario'}
      alCerrar={alCerrar}
      pie={
        <>
          <button className="btn" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="btn btn--primario"
            onClick={enviar}
            disabled={guardando || !nombre.trim() || (!usuario && (!email.trim() || password.length < 10))}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="campo">
        <label>Nombre</label>
        <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
      </div>

      {!usuario && (
        <div className="campo">
          <label>Correo</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      )}

      <div className="campo">
        <label>{usuario ? 'Nueva contraseña (opcional)' : 'Contraseña'}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <CampoPassword
            style={{ flex: 1 }}
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setGenerada(false);
            }}
            placeholder={usuario ? 'Dejar en blanco para no cambiarla' : 'Mínimo 10 caracteres'}
          />
          <button
            type="button"
            className="btn"
            title="Generar una contraseña segura"
            onClick={() => {
              setPassword(generarPassword());
              setGenerada(true);
              setCopiada(false);
            }}
          >
            <RefreshCw size={14} /> Generar
          </button>
        </div>

        {generada && (
          <div className="password-generada">
            <KeyRound size={15} style={{ color: 'var(--green-light)', flex: 'none' }} />
            <code>{password}</code>
            <button type="button" className="btn btn--sm" onClick={() => void copiar()}>
              {copiada ? <Check size={13} /> : <Copy size={13} />} {copiada ? 'Copiada' : 'Copiar'}
            </button>
          </div>
        )}

        {generada && (
          <span className="muted" style={{ fontSize: 11 }}>
            Cópiala ahora: después de guardar queda cifrada y no se puede volver a ver.
          </span>
        )}
        {usuario && password.length > 0 && (
          <span className="muted" style={{ fontSize: 11 }}>
            <KeyRound size={11} /> Cambiar la contraseña cierra todas sus sesiones abiertas.
          </span>
        )}
      </div>

      <div className="campo">
        <label>Rol</label>
        <select className="select" value={rol} onChange={(e) => setRol(e.target.value as Rol)} disabled={!puedeAsignarRoles}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ETIQUETA_ROL[r]}
            </option>
          ))}
        </select>
        {!puedeAsignarRoles && (
          <span className="muted" style={{ fontSize: 11 }}>
            Solo el super-administrador asigna roles de administración.
          </span>
        )}
      </div>

      {usuario && (
        <div className="campo">
          <label>Estado</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 13 }}>
            <input className="check" type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            Cuenta activa
          </label>
        </div>
      )}

      <div className="campo">
        <label>Grupos</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {grupos.length === 0 && <span className="muted">Todavía no hay grupos creados.</span>}
          {grupos.map((g) => (
            <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                className="check"
                type="checkbox"
                checked={miembroDe.has(g.id)}
                onChange={(e) =>
                  setMiembroDe((previo) => {
                    const siguiente = new Set(previo);
                    if (e.target.checked) siguiente.add(g.id);
                    else siguiente.delete(g.id);
                    return siguiente;
                  })
                }
              />
              {g.nombre}
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}
